import type { AbsenceKind, SchedulingStatus } from './plantafelTypes';
import { SCHEDULING_STATUS_LABELS } from './plantafelTypes';
import { absenceListTypeLabel } from './calendarListAbsences';
import { FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import { wallClockInTimeZone } from '../../utils/activityDateTime';

export type ActivityListRow = {
  id: string;
  lead_id: string | null;
  title: string;
  activity_type: string;
  due_at: string | null;
  end_at: string | null;
  done: boolean;
  lead_display_id: string | null;
  lead_name: string | null;
  scheduling_status?: string;
  site_address?: string | null;
  activity_site_address?: string | null;
  lead_site_address?: string | null;
  assignee_users?: { user_id: string; display_name: string | null; email: string | null }[];
  assignee_employees?: { id: string; first_name: string; last_name: string; color_idx?: number }[];
  list_kind?: 'activity' | 'absence';
  absence_kind?: AbsenceKind;
};

export type ActivityTypeOption = {
  id: string;
  label: string;
  icon: string;
  default_checklist_id?: string | null;
};

export type ListSortColumn =
  | 'start'
  | 'ende'
  | 'aktivitaet'
  | 'projekt'
  | 'adresse'
  | 'benutzer'
  | 'einsatzteam'
  | 'typ'
  | 'status';

export function effectiveActivityAddress(activity: ActivityListRow): string | null {
  return (
    String(
      activity.activity_site_address ?? activity.site_address ?? activity.lead_site_address ?? '',
    ).trim() || null
  );
}

export function schedulingStatusLabel(status: string | undefined | null, done: boolean): string {
  if (done) return 'Erledigt';
  const key = (status ?? 'geplant') as SchedulingStatus;
  return SCHEDULING_STATUS_LABELS[key] ?? SCHEDULING_STATUS_LABELS.geplant;
}

/** Schlichter Text statt farbiger Pills; nur Konflikte heben sich farblich ab. */
export function schedulingStatusTextClass(status: string | undefined | null, done: boolean): string {
  if (done) return 'text-app-text-subtle';
  const key = (status ?? 'geplant') as SchedulingStatus;
  if (key === 'konflikt') return 'font-medium text-rose-700';
  return 'text-app-text-muted';
}

export function getTypeLabel(typeId: string, options: ActivityTypeOption[]) {
  const absenceLabel = absenceListTypeLabel(typeId);
  if (absenceLabel) return absenceLabel;
  const list = options.length > 0 ? options : FALLBACK_ACTIVITY_TYPES;
  return list.find((t) => t.id === typeId)?.label || typeId || 'Aktivität';
}

function formatListDateTime(iso: string | null, opts: { dateOnly?: boolean }, timeZone: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  if (opts.dateOnly) {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(d);
  }
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(d);
}

export function formatListStart(dueAt: string | null, isAbsence: boolean, timeZone: string): string {
  return formatListDateTime(dueAt, { dateOnly: isAbsence }, timeZone);
}

export function formatListEnd(
  dueAt: string | null,
  endAt: string | null,
  isAbsence: boolean,
  timeZone: string,
): string {
  if (!endAt) return '—';
  if (isAbsence) {
    const end = new Date(endAt);
    if (Number.isNaN(end.getTime())) return '—';
    const w = wallClockInTimeZone(end, timeZone);
    const endInclusive =
      w.hh === 0 && w.mi === 0 && w.se === 0 ? new Date(end.getTime() - 1) : end;
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(endInclusive);
  }
  return formatListDateTime(endAt, { dateOnly: false }, timeZone);
}

export function listActivitySortValue(
  activity: ActivityListRow,
  column: ListSortColumn,
  activityTypes: ActivityTypeOption[],
): string | number {
  switch (column) {
    case 'start':
      return activity.due_at ? new Date(activity.due_at).getTime() : Number.MAX_SAFE_INTEGER;
    case 'ende':
      return activity.end_at
        ? new Date(activity.end_at).getTime()
        : activity.due_at
          ? new Date(activity.due_at).getTime()
          : Number.MAX_SAFE_INTEGER;
    case 'aktivitaet':
      return activity.title.trim().toLowerCase();
    case 'projekt':
      return activity.lead_display_id && activity.lead_name
        ? `${activity.lead_display_id} - ${activity.lead_name}`.toLowerCase()
        : '';
    case 'adresse':
      return (effectiveActivityAddress(activity) ?? '').toLowerCase();
    case 'benutzer':
      return (activity.assignee_users ?? [])
        .map((u) => u.display_name?.trim() || u.email || '')
        .filter(Boolean)
        .join(', ')
        .toLowerCase();
    case 'einsatzteam':
      return (activity.assignee_employees ?? [])
        .map((e) => `${e.first_name} ${e.last_name}`.trim())
        .join(', ')
        .toLowerCase();
    case 'typ':
      return getTypeLabel(activity.activity_type, activityTypes).toLowerCase();
    case 'status':
      return schedulingStatusLabel(activity.scheduling_status, activity.done).toLowerCase();
  }
}

export function projectLabel(activity: ActivityListRow): string {
  if (activity.lead_display_id && activity.lead_name) {
    return `${activity.lead_display_id} - ${activity.lead_name}`;
  }
  return '—';
}

export function assigneesLabel(activity: ActivityListRow): string {
  return (
    (activity.assignee_users ?? [])
      .map((u) => u.display_name?.trim() || u.email)
      .filter(Boolean)
      .join(', ') || '—'
  );
}

export function crewLabel(activity: ActivityListRow): string {
  return (
    (activity.assignee_employees ?? [])
      .map((e) => `${e.first_name} ${e.last_name}`.trim())
      .filter(Boolean)
      .join(', ') || '—'
  );
}
